import * as THREE from 'three';
import { Sky } from 'three/addons/objects/Sky.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { lightingSchema, lightingPreset, type LightingProfile } from './lighting-profile';
import type { SunShadows } from './sun-shadows';
import type { GraphicsSettings } from './graphics-settings';

export class RoomLighting {
  profile = lightingPreset('neutral');
  private sky: Sky | null = null;
  private environment: THREE.WebGLRenderTarget | null = null;
  private environmentKey = '';
  private hemi = new THREE.HemisphereLight();
  private locals: { light: THREE.PointLight | THREE.SpotLight; authored: LightingProfile['lights'][number] }[] = [];
  private emission = new WeakMap<THREE.MeshStandardMaterial, number>();
  constructor(private scene: THREE.Scene, private renderer: THREE.WebGLRenderer, private sun: SunShadows, private hdr: boolean) { scene.add(this.hemi); }
  setProfile(profile: LightingProfile): void {
    this.profile = lightingSchema.parse(profile);
    const p = this.profile;
    const azimuth = THREE.MathUtils.degToRad(p.sun.azimuth), elevation = THREE.MathUtils.degToRad(p.sun.elevation);
    const direction = new THREE.Vector3(Math.sin(azimuth)*Math.cos(elevation), Math.sin(elevation), Math.cos(azimuth)*Math.cos(elevation));
    this.sun.setLighting(direction,p.sun.color,p.sun.elevation > 0 ? p.sun.intensity : 0);
    this.hemi.color.set(p.ambient.skyColor); this.hemi.groundColor.set(p.ambient.groundColor); this.hemi.intensity = p.ambient.intensity;
    this.renderer.toneMappingExposure = p.exposure;
    const key = JSON.stringify([p.sky.mode,p.sky.turbidity,p.sky.rayleigh,p.sun.azimuth,p.sun.elevation]);
    if (key !== this.environmentKey) {
      this.clearSky(); this.environmentKey = key;
      if (p.sky.mode === 'physical') {
        this.sky = new Sky(); this.sky.scale.setScalar(10000); this.sky.frustumCulled = false;
        this.sky.renderOrder = 10000; // Render after opaque geometry so depth rejects covered sky pixels.
        const uniforms = this.sky.material.uniforms;
        // The analytic sun disk can exceed half-float's finite range. Clamp
        // before either the beauty pass or PMREM capture so infinities cannot
        // poison environment lighting and spread through bloom's blur chain.
        this.sky.material.uniforms.roomSkyIntensity={value:1};
        this.sky.material.fragmentShader = 'uniform float roomSkyIntensity;\n'+this.sky.material.fragmentShader.replace(
          'gl_FragColor = vec4( texColor, 1.0 );',
          'gl_FragColor = vec4( min( clamp( texColor, vec3( 0.0 ), vec3( 64.0 ) ) * roomSkyIntensity, vec3( 64.0 ) ), 1.0 );',
        );
        uniforms.sunPosition!.value.copy(direction);
        uniforms.turbidity!.value = p.sky.turbidity; uniforms.rayleigh!.value = p.sky.rayleigh;
        uniforms.mieCoefficient!.value = .005; uniforms.mieDirectionalG!.value = .8;
        // Clear analytic sky: skip the addon's multi-octave cloud noise in both
        // the visible sky and the reflection capture, including software GPUs.
        uniforms.cloudCoverage!.value = 0;
        this.scene.add(this.sky);
      }
      if (this.hdr) {
        const pmrem = new THREE.PMREMGenerator(this.renderer);
        if (this.sky) {
          const environmentScene = new THREE.Scene(); environmentScene.add(this.sky.clone());
          this.environment = pmrem.fromScene(environmentScene,.02,.1,20000,{size:128});
          environmentScene.clear();
        } else {
          const studio = new RoomEnvironment(); this.environment = pmrem.fromScene(studio,.04); studio.dispose();
        }
        pmrem.dispose();
      }
    }
    this.scene.environment = this.environment?.texture ?? null; this.scene.environmentIntensity = p.sky.intensity;
    this.scene.background = new THREE.Color(p.sky.color);
    this.clearLights();
    for (const authored of p.lights) {
      const light = authored.kind === 'spot' ? new THREE.SpotLight(authored.color,authored.intensity,authored.distance,THREE.MathUtils.degToRad(authored.angle),authored.penumbra,2)
        : new THREE.PointLight(authored.color,authored.intensity,authored.distance,2);
      light.position.set(authored.position.x,authored.position.y,authored.position.z);
      if (light instanceof THREE.SpotLight) {
        light.target.position.set(authored.target.x,authored.target.y,authored.target.z); this.scene.add(light.target);
        light.shadow.bias = -.00015; light.shadow.normalBias = .015; light.shadow.camera.near = .1; light.shadow.camera.far = authored.distance;
      }
      this.locals.push({light,authored}); this.scene.add(light);
    }
  }
  setQuality(quality: GraphicsSettings): void {
    const shadowBudget = quality.localShadows === 'high' ? 2 : quality.localShadows === 'low' ? 1 : 0;
    const size = quality.localShadows === 'high' ? 1024 : 512;
    let used = 0;
    for (const {light,authored} of this.locals) {
      const enabled = authored.shadow && authored.intensity > 0 && used++ < shadowBudget;
      if (light.shadow && (!enabled || light.shadow.mapSize.x !== size)) { light.shadow.map?.dispose(); light.shadow.map = null; }
      light.castShadow = enabled;
      if (light.shadow) light.shadow.mapSize.set(size,size);
    }
    const far = Math.min(quality.viewDistance,this.profile.fog.far), near = Math.min(this.profile.fog.near,far*.95);
    this.scene.fog = new THREE.Fog(this.profile.fog.color,near,far);
  }
  updateMaterials(): void {
    // Instances arrive asynchronously and own cloned materials; preserve their
    // authored emission while applying the currently selected room/local look.
    this.scene.traverse(node => {
      if (!(node instanceof THREE.Mesh)) return;
      for (const material of Array.isArray(node.material) ? node.material : [node.material]) if (material instanceof THREE.MeshStandardMaterial) {
        let original = this.emission.get(material);
        if (original === undefined) { original = material.emissiveIntensity; this.emission.set(material,original); }
        material.emissiveIntensity = original * this.profile.emissiveScale;
      }
    });
  }
  get diagnostics() { return { name: this.profile.name, sky: this.profile.sky.mode, exposure: this.profile.exposure, localLights: this.locals.length, localShadows: this.locals.filter(v => v.light.castShadow).length, bloom: this.profile.bloom.strength }; }
  private clearLights(): void {
    for (const {light} of this.locals) { light.removeFromParent(); light.shadow?.dispose(); if (light instanceof THREE.SpotLight) light.target.removeFromParent(); }
    this.locals = [];
  }
  private clearSky(): void {
    this.scene.environment = null; this.environment?.dispose(); this.environment = null;
    if (this.sky) { this.sky.removeFromParent(); this.sky.geometry.dispose(); this.sky.material.dispose(); this.sky = null; }
  }
  dispose(): void { this.clearLights(); this.clearSky(); this.hemi.removeFromParent(); }
}
